import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { PriceChange, PRICE_TYPE_LABELS } from '../../../../types';
import './Charts.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ChartsProps {
  data: PriceChange[];
}

const Charts: React.FC<ChartsProps> = ({ data }) => {
  // Chart colors
  const colors = {
    orange: '#FC7556',
    orangeLight: 'rgba(252, 117, 86, 0.2)',
    green: '#22c55e',
    greenLight: 'rgba(34, 197, 94, 0.2)',
    red: '#ef4444',
    redLight: 'rgba(239, 68, 68, 0.2)',
    blue: '#3b82f6',
    blueLight: 'rgba(59, 130, 246, 0.2)',
    purple: '#8b5cf6',
    purpleLight: 'rgba(139, 92, 246, 0.2)',
    teal: '#10b981',
    tealLight: 'rgba(16, 185, 129, 0.2)',
    gray: '#6b7280',
    grayLight: 'rgba(107, 114, 128, 0.2)'
  };

  // Changes over time data
  const changesOverTime = useMemo(() => {
    const byDay: Record<string, { increases: number; decreases: number }> = {};
    
    data.forEach(change => {
      const date = change.timestamp.split('T')[0];
      if (!byDay[date]) {
        byDay[date] = { increases: 0, decreases: 0 };
      }
      if (change.new_price > change.old_price) {
        byDay[date].increases++;
      } else if (change.new_price < change.old_price) {
        byDay[date].decreases++;
      }
    });

    const sortedDates = Object.keys(byDay).sort();
    
    return {
      labels: sortedDates.map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
      }),
      datasets: [
        {
          label: 'Aumentos',
          data: sortedDates.map(d => byDay[d].increases),
          borderColor: colors.green,
          backgroundColor: colors.greenLight,
          fill: true,
          tension: 0.4
        },
        {
          label: 'Disminuciones',
          data: sortedDates.map(d => byDay[d].decreases),
          borderColor: colors.red,
          backgroundColor: colors.redLight,
          fill: true,
          tension: 0.4
        }
      ]
    };
  }, [data]);

  // Changes by price type
  const changesByType = useMemo(() => {
    const counts: Record<string, number> = {
      restaurant: 0,
      delivery: 0,
      online: 0,
      desk: 0,
      all: 0
    };

    data.forEach(change => {
      if (counts[change.price_type] !== undefined) {
        counts[change.price_type]++;
      }
    });

    const typeColors = {
      restaurant: colors.orange,
      delivery: colors.blue,
      online: colors.purple,
      desk: colors.teal,
      all: colors.gray
    };

    const filteredTypes = Object.entries(counts).filter(([_, count]) => count > 0);

    return {
      labels: filteredTypes.map(([type]) => PRICE_TYPE_LABELS[type] || type),
      datasets: [{
        data: filteredTypes.map(([_, count]) => count),
        backgroundColor: filteredTypes.map(([type]) => typeColors[type as keyof typeof typeColors] || colors.gray),
        borderWidth: 0
      }]
    };
  }, [data]);

  // Top products by changes
  const topProducts = useMemo(() => {
    const productCounts: Record<string, { name: string; count: number }> = {};
    
    data.forEach(change => {
      if (!productCounts[change.product_id]) {
        productCounts[change.product_id] = { name: change.product_name, count: 0 };
      }
      productCounts[change.product_id].count++;
    });

    const sorted = Object.values(productCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      labels: sorted.map(p => p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name),
      datasets: [{
        label: 'Cambios',
        data: sorted.map(p => p.count),
        backgroundColor: colors.orangeLight,
        borderColor: colors.orange,
        borderWidth: 2,
        borderRadius: 6
      }]
    };
  }, [data]);

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        title: {
          display: true,
          text: 'Cantidad de Cambios',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        title: {
          display: true,
          text: 'Fecha',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 16
        }
      }
    },
    cutout: '60%'
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        title: {
          display: true,
          text: 'Cantidad de Cambios',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        title: {
          display: true,
          text: 'Productos',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        }
      }
    }
  };

  if (data.length === 0) {
    return (
      <div className="charts-empty">
        <p>No hay datos suficientes para mostrar gráficos</p>
      </div>
    );
  }

  return (
    <div className="price-charts">
      <Row>
        <Col lg={8} md={12} className="mb-4">
          <Card className="chart-card">
            <Card.Header>
              <h5>Cambios de Precio en el Tiempo</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container">
                <Line data={changesOverTime} options={lineOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={12} className="mb-4">
          <Card className="chart-card">
            <Card.Header>
              <h5>Por Tipo de Precio</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container doughnut">
                <Doughnut data={changesByType} options={doughnutOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={12} className="mb-4">
          <Card className="chart-card">
            <Card.Header>
              <h5>Top 10 Productos Más Modificados</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container bar">
                <Bar data={topProducts} options={barOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Charts;
